import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, Shield, Lock, Check, AlertTriangle, Copy, ExternalLink,
  Fingerprint, Award, Loader2, ChevronDown, ChevronUp, Scale, Clock,
  Zap, Hash
} from 'lucide-react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Amendment Card ──────────────────────────────────────
function AmendmentCard({ id, title, summary, color }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`border rounded-xl p-4 bg-[#0a0a14] ${color} transition-colors cursor-pointer`}
      onClick={() => setOpen(!open)}
      data-testid={`amendment-${id}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-xs font-mono">{id}</Badge>
          <span className="text-sm font-semibold text-white">{title}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </div>
      <AnimatePresence>
        {open && (
          <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="text-xs text-gray-400 mt-3 leading-relaxed"
          >
            {summary}
          </motion.p>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ── Signed Joinder Card ─────────────────────────────────
function JoinderCard({ j }) {
  const [copied, setCopied] = useState(false);
  const copyHash = () => {
    navigator.clipboard.writeText(j.agreement_hash);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card className="bg-[#0a0a14] border-white/5" data-testid={`joinder-${j.joinder_id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-mono text-white">{j.joinder_id}</span>
          <Badge className={j.status === 'signed' ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'}>
            {j.status === 'signed' ? 'Signed' : 'Pending'}
          </Badge>
        </div>
        <div className="space-y-2 text-xs text-gray-400">
          <div className="flex justify-between">
            <span>Module</span>
            <span className="text-gray-300 font-mono">{j.module_id || 'N/A'}</span>
          </div>
          <div className="flex justify-between">
            <span>Genesis Floor (Locked)</span>
            <span className="text-cyan-400 font-semibold">{j.genesis_floor_kutro} KOJIRU/epoch</span>
          </div>
          <div className="flex items-center justify-between">
            <span>Agreement Hash</span>
            <button onClick={copyHash} className="flex items-center gap-1 text-gray-300 hover:text-white transition-colors">
              <span className="font-mono truncate max-w-[140px]">{j.agreement_hash}</span>
              {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
            </button>
          </div>
          {j.signed_at && (
            <div className="flex justify-between">
              <span>Signed</span>
              <span className="text-gray-300">{new Date(j.signed_at).toLocaleDateString()}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Main Page ───────────────────────────────────────────
export default function ArchitectAgreement() {
  const { token } = useAuth();
  const { toast } = useToast();
  const [agreement, setAgreement] = useState(null);
  const [joinders, setJoinders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);

  // Form state
  const [step, setStep] = useState(1); // 1=review, 2=configure, 3=sign
  const [walletAddress, setWalletAddress] = useState('');
  const [moduleId, setModuleId] = useState('');
  const [genesisFloor, setGenesisFloor] = useState(50);
  const [pendingJoinder, setPendingJoinder] = useState(null);
  const [agreementExpanded, setAgreementExpanded] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [agr, jnd] = await Promise.all([
        axios.get(`${API}/api/architect-agreement/text`),
        axios.get(`${API}/api/architect-agreement/list`, { headers }),
      ]);
      setAgreement(agr.data);
      setJoinders(jnd.data.joinders || []);
    } catch (err) {
      console.error('Failed to load agreement data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Connect wallet
  const connectWallet = async () => {
    if (!window.ethereum) {
      toast({ title: 'Wallet Required', description: 'Please install MetaMask to sign the agreement.', variant: 'destructive' });
      return;
    }
    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      setWalletAddress(accounts[0]);
      toast({ title: 'Wallet Connected', description: `${accounts[0].slice(0, 6)}...${accounts[0].slice(-4)}` });
    } catch {
      toast({ title: 'Connection Failed', variant: 'destructive' });
    }
  };

  // Prepare the joinder
  const prepareJoinder = async () => {
    if (!walletAddress) {
      toast({ title: 'Connect wallet first', variant: 'destructive' });
      return;
    }
    setSigning(true);
    try {
      const { data } = await axios.post(`${API}/api/architect-agreement/prepare`, {
        architect_address: walletAddress,
        module_id: moduleId,
        genesis_floor: genesisFloor,
      }, { headers });
      setPendingJoinder(data);
      setStep(3);
      toast({ title: 'Agreement Prepared', description: `Hash: ${data.agreement_hash.slice(0, 16)}...` });
    } catch (err) {
      toast({ title: 'Preparation Failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSigning(false);
    }
  };

  // Sign and complete
  const signAgreement = async () => {
    if (!pendingJoinder || !window.ethereum) return;
    setSigning(true);
    try {
      // Request wallet signature of the agreement message
      const signature = await window.ethereum.request({
        method: 'personal_sign',
        params: [pendingJoinder.message_to_sign, walletAddress],
      });

      // Submit to backend
      const { data } = await axios.post(`${API}/api/architect-agreement/sign`, {
        joinder_id: pendingJoinder.joinder_id,
        signature,
        tx_hash: '',
      }, { headers });

      toast({ title: 'Cryptographic Joinder Complete', description: `Genesis Floor locked at ${data.genesis_floor_locked} KOJIRU/epoch` });
      setStep(1);
      setPendingJoinder(null);
      fetchData();
    } catch (err) {
      toast({ title: 'Signing Failed', description: err.message || 'User rejected signature', variant: 'destructive' });
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-8 pb-12">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
              <Scale className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white" data-testid="agreement-title">Architect Agreement</h1>
              <p className="text-sm text-gray-500">Ricardian Contract v{agreement?.version} — Cryptographic Joinder</p>
            </div>
          </div>
        </motion.div>

        {/* Amendments Section */}
        <Card className="bg-[#07070f] border-white/5" data-testid="amendments-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="w-4 h-4 text-emerald-400" />
              v2.1 Amendments (Developer Protections)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {agreement?.amendments?.map(a => (
              <AmendmentCard key={a.id} {...a} color={
                a.id === '2.1-A' ? 'border-blue-500/20 hover:border-blue-500/40' :
                a.id === '2.1-B' ? 'border-cyan-500/20 hover:border-cyan-500/40' :
                a.id === '2.1-C' ? 'border-emerald-500/20 hover:border-emerald-500/40' :
                'border-orange-500/20 hover:border-orange-500/40'
              } />
            ))}
          </CardContent>
        </Card>

        {/* Full Agreement Text */}
        <Card className="bg-[#07070f] border-white/5" data-testid="agreement-text-section">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-400" />
                Full Agreement Text
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setAgreementExpanded(!agreementExpanded)}
                className="text-xs text-gray-500 hover:text-white" data-testid="toggle-agreement-text"
              >
                {agreementExpanded ? 'Collapse' : 'Expand'} {agreementExpanded ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
              </Button>
            </div>
          </CardHeader>
          <AnimatePresence>
            {agreementExpanded && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                <CardContent>
                  <pre className="text-xs text-gray-400 whitespace-pre-wrap font-mono leading-relaxed max-h-[500px] overflow-y-auto p-4 bg-black/40 rounded-lg border border-white/5"
                    data-testid="agreement-text-content"
                  >
                    {agreement?.template}
                  </pre>
                </CardContent>
              </motion.div>
            )}
          </AnimatePresence>
        </Card>

        {/* Signing Flow */}
        <Card className="bg-[#07070f] border-white/5" data-testid="signing-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Fingerprint className="w-4 h-4 text-purple-400" />
              Cryptographic Joinder
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Step indicators */}
            <div className="flex items-center gap-2 mb-6">
              {[
                { n: 1, label: 'Review' },
                { n: 2, label: 'Configure' },
                { n: 3, label: 'Sign' },
              ].map(({ n, label }) => (
                <div key={n} className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                    step >= n ? 'bg-purple-500 text-white' : 'bg-white/5 text-gray-500'
                  }`} data-testid={`step-${n}`}>
                    {step > n ? <Check className="w-3.5 h-3.5" /> : n}
                  </div>
                  <span className={`text-xs ${step >= n ? 'text-white' : 'text-gray-500'}`}>{label}</span>
                  {n < 3 && <div className={`w-8 h-px ${step > n ? 'bg-purple-500' : 'bg-white/10'}`} />}
                </div>
              ))}
            </div>

            {/* Step 1: Review */}
            {step === 1 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                <div className="p-4 rounded-lg bg-purple-500/5 border border-purple-500/20">
                  <h4 className="text-sm font-semibold text-purple-300 mb-2">Before You Sign</h4>
                  <ul className="space-y-2 text-xs text-gray-400">
                    <li className="flex items-start gap-2"><Check className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" /> You retain 100% IP ownership of your module</li>
                    <li className="flex items-start gap-2"><Check className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" /> 0.5% perpetual revenue from module fees</li>
                    <li className="flex items-start gap-2"><Check className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" /> Genesis Floor locked at signing — DAO can't change it</li>
                    <li className="flex items-start gap-2"><Check className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" /> Maintenance ends if Sunset is triggered (no free work)</li>
                    <li className="flex items-start gap-2"><Check className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" /> "Competing Protocol" narrowly defined — Aave/Compound excluded</li>
                    <li className="flex items-start gap-2"><Check className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" /> Acquisition protection — acquirer must honor your agreement or pay 24-month buyout</li>
                    <li className="flex items-start gap-2"><Check className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" /> Ecosystem First Lock auto-expires on Change of Control</li>
                    <li className="flex items-start gap-2"><AlertTriangle className="w-3 h-3 text-yellow-400 mt-0.5 flex-shrink-0" /> 6-month exclusivity lock on Synergy-fork protocols</li>
                    <li className="flex items-start gap-2"><AlertTriangle className="w-3 h-3 text-yellow-400 mt-0.5 flex-shrink-0" /> 14-day critical bug fix window while earning revenue</li>
                  </ul>
                </div>

                <div className="p-4 rounded-lg bg-black/40 border border-white/5">
                  <p className="text-xs text-gray-500 mb-3">Excluded from "Competing Protocol" definition:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {agreement?.competing_protocol_exclusions?.map(p => (
                      <Badge key={p} variant="outline" className="text-[10px] text-gray-400 border-white/10">{p}</Badge>
                    ))}
                  </div>
                </div>

                <Button onClick={() => setStep(2)} className="w-full bg-purple-600 hover:bg-purple-500" data-testid="proceed-to-configure">
                  I've Reviewed the Terms — Proceed
                </Button>
              </motion.div>
            )}

            {/* Step 2: Configure */}
            {step === 2 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
                {/* Wallet Connection */}
                <div>
                  <Label className="text-xs text-gray-400 mb-2 block">Architect Wallet Address</Label>
                  {walletAddress ? (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/5 border border-green-500/20">
                      <Check className="w-4 h-4 text-green-400" />
                      <code className="text-sm text-green-300 font-mono">{walletAddress}</code>
                    </div>
                  ) : (
                    <Button onClick={connectWallet} variant="outline" className="w-full border-white/10 hover:bg-white/5 gap-2" data-testid="connect-wallet-btn">
                      <Fingerprint className="w-4 h-4" /> Connect Wallet
                    </Button>
                  )}
                </div>

                {/* Module ID (optional) */}
                <div>
                  <Label className="text-xs text-gray-400 mb-2 block">Module ID (optional — leave blank for general agreement)</Label>
                  <Input
                    value={moduleId} onChange={e => setModuleId(e.target.value)}
                    placeholder="MOD-XXXXXXXX"
                    className="bg-black/40 border-white/10 text-white font-mono"
                    data-testid="module-id-input"
                  />
                </div>

                {/* Genesis Floor */}
                <div>
                  <Label className="text-xs text-gray-400 mb-2 block">
                    Genesis Floor (locked at signing): <span className="text-cyan-400 font-semibold">{genesisFloor} KOJIRU/epoch</span>
                  </Label>
                  <Slider
                    value={[genesisFloor]}
                    onValueChange={([v]) => setGenesisFloor(v)}
                    min={10} max={500} step={10}
                    className="mt-2"
                    data-testid="genesis-floor-slider"
                  />
                  <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                    <span>10 KOJIRU/epoch (low bar)</span>
                    <span>500 KOJIRU/epoch (high bar)</span>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-2">
                    This is the minimum revenue your module must generate per epoch to avoid the Sunset clause.
                    Once signed, this value is <span className="text-cyan-400">permanently locked</span> and cannot be changed by DAO votes.
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setStep(1)} className="border-white/10 hover:bg-white/5">Back</Button>
                  <Button onClick={prepareJoinder} disabled={!walletAddress || signing}
                    className="flex-1 bg-purple-600 hover:bg-purple-500 gap-2" data-testid="prepare-joinder-btn"
                  >
                    {signing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Hash className="w-4 h-4" />}
                    Prepare Cryptographic Joinder
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Sign */}
            {step === 3 && pendingJoinder && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
                <div className="p-4 rounded-lg bg-[#0a0a14] border border-purple-500/20">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Agreement Hash (SHA-256)</p>
                  <code className="text-sm text-purple-300 font-mono break-all" data-testid="agreement-hash">{pendingJoinder.agreement_hash}</code>
                </div>

                <div className="p-4 rounded-lg bg-[#0a0a14] border border-white/5 space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Joinder ID</span>
                    <span className="text-white font-mono">{pendingJoinder.joinder_id}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Genesis Floor (Locked)</span>
                    <span className="text-cyan-400 font-semibold">{pendingJoinder.genesis_floor_kutro} KOJIRU/epoch</span>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-yellow-500/5 border border-yellow-500/20">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-yellow-300 font-semibold">Legally Binding</p>
                      <p className="text-[10px] text-yellow-200/60 mt-1">
                        Clicking "Sign" will request a wallet signature. This creates an immutable, legally binding record that you agree to the Architect Agreement v{agreement?.version}.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => { setStep(2); setPendingJoinder(null); }}
                    className="border-white/10 hover:bg-white/5">Back</Button>
                  <Button onClick={signAgreement} disabled={signing}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:brightness-110 gap-2" data-testid="sign-agreement-btn"
                  >
                    {signing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Fingerprint className="w-4 h-4" />}
                    Sign Cryptographic Joinder
                  </Button>
                </div>
              </motion.div>
            )}
          </CardContent>
        </Card>

        {/* Existing Joinders */}
        {joinders.length > 0 && (
          <div data-testid="joinders-list">
            <h2 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
              <Award className="w-4 h-4 text-purple-400" />
              Your Signed Agreements ({joinders.length})
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {joinders.map(j => <JoinderCard key={j.joinder_id} j={j} />)}
            </div>
          </div>
        )}

        {/* Public Verification Info */}
        <Card className="bg-[#07070f] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-gray-500" />
              <div>
                <p className="text-xs text-gray-400">All signed Architect Agreements are publicly verifiable at</p>
                <code className="text-xs text-purple-400 font-mono">/api/architect-agreement/verify/JND-XXXXXXXX</code>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
